import { Upload, Check } from 'lucide-react'
import { T } from '../theme'
import { SCOPE_SINGLE_HALF, SCOPE_TWO_FILES } from './VideoScopeChoice'

/**
 * VideoDropSlots — un emplacement par fichier attendu.
 * PARCOURS-VIDEO-DECLARE-002.
 *
 * POURQUOI DES EMPLACEMENTS NOMMES, ET PAS UNE SELECTION GROUPEE.
 *
 * La version precedente demandait au coach de choisir ses deux fichiers d'un
 * coup. Ca ne tient pas en pratique · les deux ne sont pas toujours dans le
 * meme dossier, et le second est souvent encore en cours de transfert depuis le
 * telephone quand le premier est deja pret.
 *
 * Deux emplacements nommes — « 1re période » et « 2e période » — laissent le
 * coach n'en remplir qu'un, envoyer, et revenir plus tard pour l'autre. C'est
 * le meme principe que le bandeau de reprise · on ne lui redemande jamais ce
 * qu'il a deja donne.
 *
 * `dejaEnvoyes` marque les periodes DEJA ARRIVEES sur le serveur · leur
 * emplacement affiche un etat, pas un selecteur. C'est ce qui permet de reutiliser
 * ce composant sur la page du match, ou MT1 peut etre en base depuis hier.
 */

function libelles(scope) {
  if (scope === SCOPE_TWO_FILES) {
    return [
      { titre: '1re période', aide: 'Le fichier de la première mi-temps.' },
      { titre: '2e période',  aide: 'Le fichier de la seconde mi-temps.' },
    ]
  }
  if (scope === SCOPE_SINGLE_HALF) {
    return [{ titre: 'La période filmée', aide: 'MP4 ou MOV, jusqu\'à 8 Go.' }]
  }
  return [{ titre: 'La vidéo du match', aide: 'MP4 ou MOV, jusqu\'à 8 Go.' }]
}

export default function VideoDropSlots({
  scope,
  fichiers = [],
  dejaEnvoyes = {},
  onChoisir,
  onRetirer,
  disabled = false,
}) {
  const cases = libelles(scope)
  const multiple = cases.length > 1

  return (
    <div
      data-testid="video-slots"
      style={{
        display: 'grid',
        gridTemplateColumns: multiple ? 'repeat(auto-fit, minmax(220px, 1fr))' : '1fr',
        gap: 12, marginBottom: 20,
      }}
    >
      {cases.map((c, i) => {
        const fichier = fichiers[i] || null
        const envoye = Boolean(dejaEnvoyes[i])
        const rempli = envoye || Boolean(fichier)
        return (
          <div
            key={c.titre}
            style={{
              border: `${rempli ? 1 : 2}px ${rempli ? 'solid' : 'dashed'} ${rempli ? T.goldBdr : T.rule}`,
              background: rempli ? T.goldBg : T.surface,
              padding: '18px 16px',
              display: 'flex', flexDirection: 'column', gap: 8,
            }}
          >
            <div style={{
              fontFamily: T.mono, fontSize: 9, letterSpacing: '.16em',
              textTransform: 'uppercase', color: rempli ? T.gold : T.muted,
            }}>
              {c.titre}
            </div>

            {envoye ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Check size={14} color={T.gold} />
                <span style={{ fontFamily: T.mono, fontSize: 11, color: T.ink }}>
                  Déjà envoyée
                </span>
              </div>
            ) : fichier ? (
              <>
                <span style={{
                  fontFamily: T.mono, fontSize: 11, color: T.ink, fontWeight: 700,
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                }}>
                  {fichier.name}
                </span>
                <span style={{ fontFamily: T.mono, fontSize: 10, color: T.muted }}>
                  {(fichier.size / (1024 * 1024)).toFixed(1)} MB
                </span>
                {!disabled && (
                  <button
                    type="button"
                    onClick={() => onRetirer(i)}
                    style={{
                      alignSelf: 'flex-start', marginTop: 2,
                      background: 'transparent', border: `1px solid ${T.rule}`,
                      padding: '5px 10px', fontFamily: T.mono, fontSize: 9,
                      letterSpacing: '.1em', textTransform: 'uppercase',
                      color: T.muted, cursor: 'pointer',
                    }}
                  >
                    Retirer
                  </button>
                )}
              </>
            ) : (
              <>
                <span style={{ fontFamily: T.mono, fontSize: 10, color: T.muted, lineHeight: 1.5 }}>
                  {c.aide}
                </span>
                <label style={{
                  alignSelf: 'flex-start', marginTop: 2,
                  display: 'flex', alignItems: 'center', gap: 7,
                  padding: '9px 14px', background: T.dark, color: T.surface,
                  fontFamily: T.mono, fontSize: 9, letterSpacing: '.1em',
                  textTransform: 'uppercase',
                  cursor: disabled ? 'not-allowed' : 'pointer',
                  opacity: disabled ? 0.5 : 1,
                }}>
                  {/* Un selecteur PAR EMPLACEMENT, jamais `multiple` · c'est ce
                      qui permet de n'en remplir qu'un et de revenir plus tard. */}
                  <input
                    type="file"
                    accept=".mp4,.mov,.m4v,video/mp4,video/quicktime"
                    disabled={disabled}
                    onChange={(e) => {
                      const f = e.target.files?.[0]
                      e.target.value = ''
                      if (f) onChoisir(i, f)
                    }}
                    style={{ display: 'none' }}
                  />
                  <Upload size={12} /> Choisir
                </label>
              </>
            )}
          </div>
        )
      })}
    </div>
  )
}
